'use client';

import { c as _c } from "react/compiler-runtime";
import { Price } from '@7f-ui/components/price';
import { formatDateWithYearShort, useDateLabel } from '@7f-ui/hooks/use-date-label';
import { addedToCartItemsAtom, buildExperienceCartItem } from '@feat/feat-cart/added-to-cart';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useCartItem } from '@feat/feat-cart/item';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { WishlistToggle } from '@feat/feat-wishlists/toggle';
import { CalendarBlank, CardsThree, User } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useAppExperiencePersonsLabel } from '../_hooks/use-app-experience-persons-label';
import { useCurrentPathSearch } from '../_hooks/use-current-path-search';
import { buildTermineHref, type EdpTimeslot, getTotalCount } from '../_lib/experience-booking-utils';
import type { Experience } from '../_lib/experience-state-types';
import { AppBookingBox } from './app-booking-box';
import { AppBookingQueryError, BOOKING_AVAILABILITY_ERROR, type BookingQueryError } from './app-booking-query-error';
import { AppExperienceCancelation } from './app-experience-cancelation';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceRating } from './app-experience-rating';
import { useEdpDateSelectionArm } from './edp-date-selection-arm';
type AppExperienceBookingBoxDesktopProps = {
  price: {
    price: number;
    category: string;
  } | null;
  selectedTimeslot: EdpTimeslot | null;
  location: string;
  hasTimeslots: boolean;
  bookingError?: BookingQueryError;
  bookingPending?: boolean;
  hasMultipleVariants: boolean;
  hasAvailableDates: boolean;
  userTimeslotRequestsAllowed?: boolean | null;
  onVariantClick: () => void;
  onPaxClick: () => void;
  onDateClick: () => void;
  onOpenReviews: () => void;
  onCtaClick?: () => void;
};
export function AppExperienceBookingBoxDesktop(t0) {
  const $ = _c(62);
  const {
    price,
    selectedTimeslot: edpSelectedTimeslot,
    location,
    bookingError,
    bookingPending,
    hasTimeslots,
    hasMultipleVariants,
    hasAvailableDates,
    userTimeslotRequestsAllowed,
    onVariantClick,
    onPaxClick,
    onDateClick,
    onOpenReviews,
    onCtaClick
  } = t0;
  const {
    filter,
    experience
  } = useExperienceFilterContext();
  const {
    allOptions
  } = useExperienceOptions(experience.variants);
  const {
    isVariant
  } = useEdpDateSelectionArm();
  const setAddedToCartItems = useSetAtom(addedToCartItemsAtom);
  const {
    addExperience
  } = useCartItem();
  const showError = !bookingError && !bookingPending && !hasAvailableDates && hasTimeslots;
  const isCurrencyMismatch = useIsCartCurrencyMismatch(experience.currency);
  let t1;
  if ($[0] !== bookingError || $[1] !== bookingPending || $[2] !== filter.selectedOptions || $[3] !== isCurrencyMismatch || $[4] !== showError) {
    t1 = getTotalCount(filter.selectedOptions) === 0 || showError || isCurrencyMismatch || !!bookingError || bookingPending;
    $[0] = bookingError;
    $[1] = bookingPending;
    $[2] = filter.selectedOptions;
    $[3] = isCurrencyMismatch;
    $[4] = showError;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const isCtaDisabled = t1;
  const hasVariantSelection = isVariant && !!edpSelectedTimeslot;
  let dateMessage;
  if (bookingError?.availabilityFailed) {
    dateMessage = BOOKING_AVAILABILITY_ERROR;
  } else {
    if (showError) {
      dateMessage = "Keine freien Termine gefunden. Bitte \xE4ndere den Zeitraum.";
    }
  }
  let t2;
  if ($[6] !== addExperience || $[7] !== edpSelectedTimeslot || $[8] !== experience || $[9] !== filter.selectedOptions || $[10] !== isCtaDisabled || $[11] !== setAddedToCartItems) {
    t2 = () => {
      if (isCtaDisabled || !edpSelectedTimeslot || !filter.selectedOptions) {
        return;
      }
      addExperience({
        timeslotId: edpSelectedTimeslot.id,
        experienceId: experience.id,
        selectedOptions: filter.selectedOptions
      }, {
        onSuccess: () => {
          setAddedToCartItems([buildExperienceCartItem(experience, {
            startDate: edpSelectedTimeslot.date,
            startTimezone: edpSelectedTimeslot.startTimezone
          })]);
        }
      });
    };
    $[6] = addExperience;
    $[7] = edpSelectedTimeslot;
    $[8] = experience;
    $[9] = filter.selectedOptions;
    $[10] = isCtaDisabled;
    $[11] = setAddedToCartItems;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleAddToCart = t2;
  let t3;
  if ($[13] !== filter.from || $[14] !== filter.to) {
    t3 = {
      fromDate: filter.from,
      toDate: filter.to,
      formatFromOptions: formatDateWithYearShort,
      formatToOptions: formatDateWithYearShort,
      enablePlaceholder: false
    };
    $[13] = filter.from;
    $[14] = filter.to;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const dateLabel = useDateLabel(t3);
  let t4;
  if ($[16] !== allOptions || $[17] !== filter.selectedOptions) {
    t4 = {
      allOptions,
      selectedOptions: filter.selectedOptions,
      fallback: "Personen"
    };
    $[16] = allOptions;
    $[17] = filter.selectedOptions;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const paxLabel = useAppExperiencePersonsLabel(t4);
  let t5;
  if ($[19] !== experience.variants || $[20] !== filter.selectedVariant) {
    t5 = experience.variants.find(variant => variant.id === filter.selectedVariant)?.title;
    $[19] = experience.variants;
    $[20] = filter.selectedVariant;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const variantSelectLabel = t5;
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = shopTestId("ExperienceBookingBox.container");
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== experience.currency || $[24] !== experience.hasMultiplePrices || $[25] !== price) {
    t7 = !!price && <AppBookingBox.Title><h3 className="font-heading3">{experience.hasMultiplePrices ? "ab " : null}<Price amount={price.price} currency={experience.currency} />{" "}<span className="font-body-small font-normal">{"pro "} {price.category}</span></h3></AppBookingBox.Title>;
    $[23] = experience.currency;
    $[24] = experience.hasMultiplePrices;
    $[25] = price;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== bookingError) {
    t8 = bookingError?.priceFailed && <AppBookingBox.Title><AppBookingQueryError error={bookingError} availabilityMessageInField={true} /></AppBookingBox.Title>;
    $[27] = bookingError;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== bookingError || $[30] !== dateLabel || $[31] !== dateMessage || $[32] !== experience || $[33] !== handleAddToCart || $[34] !== hasMultipleVariants || $[35] !== hasTimeslots || $[36] !== hasVariantSelection || $[37] !== isCtaDisabled || $[38] !== location || $[39] !== onCtaClick || $[40] !== onDateClick || $[41] !== onPaxClick || $[42] !== onVariantClick || $[43] !== paxLabel || $[44] !== showError || $[45] !== userTimeslotRequestsAllowed || $[46] !== variantSelectLabel) {
    t9 = hasTimeslots ? <>{hasMultipleVariants && <AppBookingBox.Field label="Option"><AppBookingBox.Button Icon={CardsThree} onClick={() => {
          trackClickCta("option", {
            type: "experience"
          });
          onVariantClick();
        }} data-testid={shopTestId("ExperienceBookingBox.variant")}><span data-testid={shopTestId("ExperienceBookingBox.variant-label")}>{variantSelectLabel}</span></AppBookingBox.Button></AppBookingBox.Field>}<AppBookingBox.Field label="Personen"><AppBookingBox.Button Icon={User} onClick={() => {
          trackClickCta("person selector", {
            type: "experience"
          });
          onPaxClick();
        }} data-testid={shopTestId("ExperienceBookingBox.pax")}><span data-testid={shopTestId("ExperienceBookingBox.pax-label")}>{paxLabel}</span></AppBookingBox.Button></AppBookingBox.Field><AppBookingBox.Field label={onCtaClick ? "Datum" : "Reisezeitraum"}><AppBookingBox.Button isError={bookingError?.availabilityFailed || showError} message={dateMessage} Icon={CalendarBlank} onClick={() => {
          trackClickCta("calendar", {
            type: "experience"
          });
          onDateClick();
        }} data-testid={shopTestId("ExperienceBookingBox.date")}><span data-testid={shopTestId("ExperienceBookingBox.date-label")}>{dateLabel}</span></AppBookingBox.Button></AppBookingBox.Field>{bookingError?.availabilityFailed && !bookingError.priceFailed && <AppBookingQueryError error={bookingError} availabilityMessageInField={true} />}{hasVariantSelection && <AppExperienceCancelation cancelationHours={experience.cancelationDeadline} variant="success" />}<DesktopCtaWithWishlist hasTimeslots={true} experience={experience} location={location} disabled={isCtaDisabled} ctaLabel={hasVariantSelection ? "In den Warenkorb" : undefined} onCtaClick={hasVariantSelection ? handleAddToCart : onCtaClick && (() => {
        trackClickCta("check availability", {
          type: "experience"
        });
        onCtaClick();
      })} onShowDatesClick={_temp} /></> : <>{userTimeslotRequestsAllowed ? <div className="rounded-lg bg-lightblue p-4"><p className="font-body-small">Dieses Erlebnis ist nur auf Anfrage buchbar. Das heißt für dich: Totale Flexibilität bei der Terminauswahl. Einfach Wunschdatum eingeben, absenden und schon bald dein Erlebnis zur perfekten Zeit genießen.</p></div> : <p className="font-semibold">Dieses Erlebnis ist nur auf Anfrage buchbar.</p>}<DesktopCtaWithWishlist experience={experience} location={location} /></>;
    $[29] = bookingError;
    $[30] = dateLabel;
    $[31] = dateMessage;
    $[32] = experience;
    $[33] = handleAddToCart;
    $[34] = hasMultipleVariants;
    $[35] = hasTimeslots;
    $[36] = hasVariantSelection;
    $[37] = isCtaDisabled;
    $[38] = location;
    $[39] = onCtaClick;
    $[40] = onDateClick;
    $[41] = onPaxClick;
    $[42] = onVariantClick;
    $[43] = paxLabel;
    $[44] = showError;
    $[45] = userTimeslotRequestsAllowed;
    $[46] = variantSelectLabel;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] !== experience.publicId || $[49] !== onOpenReviews) {
    t10 = <AppExperienceRating publicId={experience.publicId} className="justify-center" onClick={onOpenReviews} />;
    $[48] = experience.publicId;
    $[49] = onOpenReviews;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] !== t10 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t11 = <AppBookingBox>{t7}{t8}{t9}{t10}</AppBookingBox>;
    $[51] = t10;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== experience.cancelationDeadline || $[57] !== hasVariantSelection) {
    t12 = !hasVariantSelection && <div className="mt-6"><AppExperienceCancelation cancelationHours={experience.cancelationDeadline} variant="outlined" /></div>;
    $[56] = experience.cancelationDeadline;
    $[57] = hasVariantSelection;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== t11 || $[60] !== t12) {
    t13 = <div className="sticky-top-offset-10 sticky" data-testid={t6}>{t11}{t12}</div>;
    $[59] = t11;
    $[60] = t12;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
}
function _temp() {
  trackClickCta("show dates", {
    type: "experience"
  });
}
type DesktopCtaWithWishlistProps = {
  experience: Experience;
  location: string;
  hasTimeslots?: boolean;
  disabled?: boolean;
  ctaLabel?: string;
  onCtaClick?: () => void;
  onShowDatesClick?: () => void;
};
function DesktopCtaWithWishlist(t0) {
  const $ = _c(22);
  const {
    hasTimeslots,
    experience,
    location,
    disabled,
    ctaLabel,
    onCtaClick,
    onShowDatesClick
  } = t0;
  const {
    basePath,
    search
  } = useCurrentPathSearch();
  let t1;
  if ($[0] !== basePath || $[1] !== search) {
    t1 = buildTermineHref(basePath, search);
    $[0] = basePath;
    $[1] = search;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const termineHref = t1;
  const requestHref = `/service/anfrageformular-erlebnisse/?experienceId=${experience.publicId}&subject=APPOINTMENT_REQUEST`;
  const label = ctaLabel ?? "Verf\xFCgbarkeit pr\xFCfen";
  let t2;
  if ($[3] !== disabled || $[4] !== hasTimeslots || $[5] !== label || $[6] !== onCtaClick || $[7] !== onShowDatesClick || $[8] !== requestHref || $[9] !== termineHref) {
    t2 = () => {
      if (!hasTimeslots) {
        return <AppBookingBox.CTA full={true} as={Link} href={requestHref}>Termin anfragen</AppBookingBox.CTA>;
      }
      if (onCtaClick) {
        return <AppBookingBox.CTA full={true} data-testid={shopTestId("ExperienceBookingBox.cta")} disabled={disabled} onClick={onCtaClick}>{label}</AppBookingBox.CTA>;
      }
      return <AppBookingBox.CTA full={true} data-testid={shopTestId("ExperienceBookingBox.cta")} disabled={disabled} as={Link} href={termineHref} onClick={onShowDatesClick}>{label}</AppBookingBox.CTA>;
    };
    $[3] = disabled;
    $[4] = hasTimeslots;
    $[5] = label;
    $[6] = onCtaClick;
    $[7] = onShowDatesClick;
    $[8] = requestHref;
    $[9] = termineHref;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const getCta = t2;
  let t3;
  if ($[11] !== getCta) {
    t3 = getCta();
    $[11] = getCta;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = experience.images?.[0]?.url;
  let t5;
  if ($[13] !== experience.id || $[14] !== experience.publicId || $[15] !== experience.title || $[16] !== location || $[17] !== t4) {
    t5 = <WishlistToggle productId={experience.id} publicProductId={experience.publicId} productType="experience" productName={experience.title} locationName={location} image={t4} origin="pdp" />;
    $[13] = experience.id;
    $[14] = experience.publicId;
    $[15] = experience.title;
    $[16] = location;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t3 || $[20] !== t5) {
    t6 = <div className="flex items-center gap-4">{t3}{t5}</div>;
    $[19] = t3;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}